import { Link } from 'react-router-dom';
import { Shield, LogOut, LogIn, UserPlus } from 'lucide-react';

interface NavigationItem {
  id: string;
  label: string;
  url: string;
  is_dropdown: boolean;
  is_external: boolean;
}

interface NavigationSubItem {
  id: string;
  label: string;
  url: string;
  parent_id: string;
  is_external: boolean;
}

interface DocumentCategory {
  name: string;
  slug: string;
  icon: string;
  subcategories: {
    name: string;
    slug: string;
  }[];
}

interface MobileMenuProps {
  isOpen: boolean;
  isAuthenticated: boolean;
  userDisplayName: string;
  isAdmin: boolean;
  userRole?: string;
  navItems: NavigationItem[];
  navSubItems: NavigationSubItem[];
  categories: DocumentCategory[];
  openDropdown: string | null;
  handleDropdownClick: (slug: string, e?: React.MouseEvent) => void;
  onClose: () => void;
  onLogout: () => void;
}

export function MobileMenu({
  isOpen,
  isAuthenticated,
  userDisplayName,
  isAdmin,
  userRole,
  navItems,
  navSubItems,
  categories,
  openDropdown,
  handleDropdownClick,
  onClose,
  onLogout
}: MobileMenuProps) {
  if (!isOpen) return null;

  // Get subitems for a specific parent item
  const getSubItems = (parentId: string) => {
    return navSubItems.filter(item => item.parent_id === parentId);
  };

  return (
    <div className="lg:hidden fixed inset-0 top-[68px] bg-white dark:bg-gray-900 z-[60] overflow-y-auto pb-20">
      <nav className="flex flex-col space-y-4 p-4 border-t border-gray-200 dark:border-gray-700 animate-fade-in">
        {/* User Authentication Section for Mobile */}
        <div className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-2">
          {isAuthenticated ? (
            <div className="space-y-3">
              <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Logado como
                </p>
                <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                  {userDisplayName}
                </p>
                {userRole && (
                  <div className="flex items-center mt-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                      <Shield className="w-3 h-3 mr-1" />
                      {userRole}
                    </span>
                  </div>
                )}
              </div>
              
              {isAdmin && (
                <Link
                  to="/admin"
                  className="flex items-center px-4 py-2 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 rounded-lg"
                  onClick={onClose}
                >
                  <Shield className="w-4 h-4 mr-3" />
                  <span>Painel Administrativo</span>
                </Link>
              )}
              
              <button
                onClick={() => {
                  onLogout();
                  onClose();
                }}
                className="w-full flex items-center px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg text-left cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-3" />
                <span>Sair</span>
              </button>
            </div>
          ) : (
            <div className="flex flex-col space-y-2">
              <Link
                to="/login"
                className="flex items-center px-4 py-2 bg-primary-50 dark:bg-primary-900/20 text-primary-900 dark:text-primary-900 rounded-lg"
                onClick={onClose}
              >
                <LogIn className="w-4 h-4 mr-3" />
                <span>Entrar</span>
              </Link>
              
              <Link
                to="/registrar"
                className="flex items-center px-4 py-2 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg"
                onClick={onClose}
              >
                <UserPlus className="w-4 h-4 mr-3" />
                <span>Registrar</span>
              </Link>
            </div>
          )}
        </div>

        {/* Use navItems if available, otherwise fallback */}
        {navItems.length > 0 ? 
          navItems.map((link) => (
            <div key={link.id}>
              {link.is_dropdown ? (
                // Dropdown item in mobile menu
                <>
                  <div 
                    onClick={(e) => handleDropdownClick(link.id as string, e)}
                    className="flex items-center justify-between w-full px-4 py-2 text-gray-700 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
                  >
                    <span>{link.label}</span>
                    <svg 
                      className={`w-4 h-4 transition-transform ${openDropdown === link.id ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  
                  {openDropdown === link.id && (
                    <div className="mt-1 ml-4 pl-2 border-l-2 border-gray-200 dark:border-gray-700 space-y-1">
                      {getSubItems(link.id as string).map((subItem) => (
                        <Link
                          key={subItem.id}
                          to={subItem.url}
                          className="block px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                          onClick={onClose}
                          target={subItem.is_external ? "_blank" : undefined}
                          rel={subItem.is_external ? "noopener noreferrer" : undefined}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                // Regular link in mobile menu
                <Link
                  to={link.url}
                  className="px-4 py-2 text-gray-700 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  onClick={onClose}
                  target={link.is_external ? "_blank" : undefined}
                  rel={link.is_external ? "noopener noreferrer" : undefined}
                >
                  {link.label}
                </Link>
              )}
            </div>
          )) : 
          [
            { text: 'Serviços', path: '/servicos' },
            { text: 'Notícias', path: '/noticias' },
            { text: 'Licitações', path: '/licitacoes' },
            { text: 'Concursos', path: '/concursos' },
            { text: 'Contato', path: '/contato' },
          ].map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="px-4 py-2 text-gray-700 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
              onClick={onClose}
            >
              {link.text}
            </Link>
          ))
        }

        {/* Mobile Documents Menu */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <h3 className="px-4 py-2 text-sm font-semibold uppercase text-gray-500 dark:text-gray-400">
            Documentos Oficiais
          </h3>
          {categories.map((category) => (
            <div key={category.slug} className="space-y-2 mt-2">
              <Link
                to={`/documentos/${category.slug}`}
                className="block px-4 py-2 text-gray-700 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors font-medium"
                onClick={onClose}
              >
                {category.name}
              </Link>
              <div className="pl-8 space-y-2">
                {category.subcategories.map((sub) => (
                  <Link
                    key={sub.slug}
                    to={`/documentos/${category.slug}/${sub.slug}`}
                    className="block px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm"
                    onClick={onClose}
                  >
                    {sub.name}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </nav>
    </div>
  );
}